import React, { FC, useEffect, useState } from 'react';
import './App.css';
import AccessSwitchesPage from "./pages/AccessSwitchesPage";
import { createStyles, makeStyles, Theme, Toolbar } from "@material-ui/core";
import AppBar from "@material-ui/core/AppBar";
import Typography from "@material-ui/core/Typography";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import LockOpen from "@material-ui/icons/LockOpen"
import AddServicePage from "./pages/AddServicePage";
import SpidSwitchPage from "./pages/SpidSwitchPage";
import "./api-client/mocks"
import createTheme from "@material-ui/core/styles/createTheme";
import CssBaseline from "@material-ui/core/CssBaseline";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { ReactComponent as Logo } from './images/svd-logo.svg';
import Schibsted_Logo from './images/schibsted-logo.png';
import Schibsted_Logo_Dark from './images/schibsted-logo-dark.png';
import ResponseMessagesPage from "./pages/ResponseMessagesPage";

import LoginPage from "./pages/LoginPage";
import { ThemeProvider } from '@material-ui/styles';
import { blue, indigo } from "@material-ui/core/colors";
import { invokeLogout } from "./api-client/client";
import Button from "@material-ui/core/Button";

interface CopyrightProps {
    darkMode: boolean
}

function Copyright(props: CopyrightProps) {
    return (
        <Typography component={ 'div' } variant="body2" color="textSecondary" align="center">
            <div>
                <img src={ props.darkMode ? Schibsted_Logo_Dark : Schibsted_Logo } alt="Schibsted_Logo" height={ 40 }/>
            </div>
            { 'Copyright © ' }
            { new Date().getFullYear() }
            { ' | Powered by E-TECH' }
        </Typography>
    );
}

const darkTheme = createTheme({
    palette: {
        type: 'dark',
        primary: blue
    },
    typography: {
        h5: {
            fontWeight: 500,
            letterSpacing: 0.5
        }
    },
});

const lightTheme = createTheme({
    palette: {
        type: 'light',
        primary: indigo
    },
    typography: {
        h5: {
            fontWeight: 500,
            letterSpacing: 0.5
        }
    },
});

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            display: 'flex',
            minHeight: '100vh',
        },
        app: {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: "auto",
        },
        main: {
            flex: 1,
            padding: darkTheme.spacing(2, 4),
        },
        footer: {
            padding: darkTheme.spacing(2),
        },
        title: {
            flexGrow: 1
        },
        logout: {
            marginRight: 10
        }
    }));


const App: FC = () => {
    const classes = useStyles()
    const navigateTo = useNavigate()
    const location = useLocation()

    const [ loggedIn, setLoggedIn ] = useState(detectIfLoggedIn());
    const [ isAdmin, setIsAdmin ] = useState(hasAdminPermissions(readPermissions()));
    const [ darkMode, setDarkMode ] = useState(darkModeEnabled());


    const [ value, setValue ] = React.useState(location.pathname);

    const handleTabChange = (event: any, newValue: React.SetStateAction<string>) => {
        if(location.pathname !== newValue.valueOf())
            navigateTo(newValue.valueOf())
        setValue(newValue);
    };

    useEffect(() => {
        const listener = (ev: MediaQueryListEvent) => setDarkMode(ev.matches);
        const matchMedia = window.matchMedia('(prefers-color-scheme: dark)');
        const eventListenerMethodsPresent = matchMedia.addEventListener !== undefined;
        eventListenerMethodsPresent ? matchMedia.addEventListener('change', listener)
            : matchMedia.addListener(listener);

        return () => {
            eventListenerMethodsPresent ? matchMedia.removeEventListener('change', listener)
                : matchMedia.removeListener(listener);
        };
    });

    function darkModeEnabled(): boolean {
        return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    }

    function detectIfLoggedIn(): boolean {
        return window.localStorage.getItem("svd-permissions") != null;
    }

    function readPermissions(): string[] {
        const permissions = window.localStorage.getItem("svd-permissions") || "[]";
        return JSON.parse(permissions);
    }

    function login(permissions: string[]) {
        localStorage.setItem("svd-permissions", JSON.stringify(permissions));
        setIsAdmin(hasAdminPermissions(permissions));
        setLoggedIn(true);
    }

    function hasAdminPermissions(permissions: string[]) {
        return permissions.includes("ADMIN") || permissions.includes("SUPPORT");
    }

    function logoutClicked() {
        invokeLogout().then(() => {
            localStorage.removeItem("svd-permissions");
            setLoggedIn(false);
            navigateTo("/")
            setValue("/")
        }).catch(err => {
            console.error(err);
        });
    }

    return (<ThemeProvider theme={darkMode ? darkTheme : lightTheme}>
        <React.Fragment>
            <CssBaseline/>
            <div className={classes.root}>
                <div className={classes.app}>
                    <AppBar position="static" color={ "default" }>
                        <Toolbar>
                            <Typography variant="h5" color="inherit" noWrap className={ classes.title }>
                                SvD Access <LockOpen/>
                            </Typography>
                            { loggedIn && <Button className={ classes.logout } onClick={ logoutClicked }>Logout</Button> }
                            <Logo/>
                        </Toolbar>
                        { loggedIn && isAdmin &&
                            <Tabs
                                value={ value }
                                onChange={ handleTabChange }
                                indicatorColor={ "primary" }
                            >
                                <Tab label="Emergency access control" value={ "/" }/>
                                <Tab label="Response messages" value={ "/response-messages" }/>
                                <Tab label="SPID switch" value={ "/spid" } id={ "spid-tab-button" }/>
                            </Tabs> }
                    </AppBar>
                    <main className={ classes.main }>
                        <Container maxWidth={ loggedIn ? "md" : "xs" }>
                            <Grid container spacing={ 3 }>
                                <Grid item xs={ 12 }>
                                    { loggedIn ? (
                                        <Routes>
                                            <Route path="/" element={ <AccessSwitchesPage isAdmin={ isAdmin }/> }/>
                                            <Route path="/access/create" element={ <AddServicePage/> }/>
                                            <Route path="/spid" element={ <SpidSwitchPage/> }/>
                                            <Route path="/response-messages" element={ <ResponseMessagesPage/> }/>
                                            <Route path={ "*" } element={ <Navigate replace to={ "/" }/> }/>
                                        </Routes>
                                    ) : <LoginPage login={ login }/> }
                                </Grid>
                            </Grid>
                        </Container>
                    </main>
                    <footer className={ classes.footer }>
                        <Copyright darkMode={ darkMode }/>
                    </footer>
                </div>
            </div>
        </React.Fragment>
        </ThemeProvider>
    );
};

export default App;
